import { useEffect, useMemo, useRef, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';

import { useServiceFeatures } from 'hooks/useServiceContext';
import { Maybe, Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';

import { LTLItem, LTLUnitSystem, LTL_FREIGHT_CLASSES } from '../../../types';
import { calculateLTLMetrics } from '../../../utils/ltlMetrics';

/** Shared hook: true when Tai TMS is among LTL integrations. */
export function useIsTaiTMS(): boolean {
  const { ltlTMSIntegrations } = useServiceFeatures();
  return (
    ltlTMSIntegrations?.some((integration) => integration.name === TMS.Tai) ??
    false
  );
}

interface UseTaiFreightClassWarningProps {
  items: LTLItem[];
  units: LTLUnitSystem;
  // Matches `ltl.commodity.stackable` — vertical stacking preprocessing for layout.
  stackingEnabled?: boolean;
  // Controls cubic-feet basis for estimated class:
  // - true: layout bounding box (shipment-like)
  // - false: per-item sum (item-card consistency)
  useLayoutBoundingBoxForCubicFeet?: boolean;
  // Current freight class value (e.g. for this item or commodity).
  selectedFreightClass: Undef<string>;
  // True when user explicitly chose "Use estimated class".
  usingEstimatedFreightClass?: Undef<boolean>;
  setValue: UseFormSetValue<any>;
  // Form path to write freight class (e.g. `ltl.items.0.freightClass`). If not set, uses `ltl.commodity.freightClass`.
  freightClassPath?: Undef<string>;
  // Form path to write estimated freight class (e.g. `ltl.items.0.estimatedFreightClass`).
  estimatedFreightClassPath?: Undef<string>;
  // Form path to write whether estimated class is explicitly used.
  usingEstimatedFreightClassPath?: Undef<string>;
  // Form path to write whether the estimated class warning is shown.
  showEstimatedFreightClassMessagePath?: Undef<string>;
}

interface UseTaiFreightClassWarningReturn {
  calculatedFreightClass: Maybe<string>;
  showWarning: boolean;
  handleUseEstimatedClass: () => void;
  handleDismissWarning: () => void;
}

/**
 * Custom hook for freight class calculation and warning logic.
 * Handles auto-population, change detection, and warning display.
 */
export function useTaiFreightClassWarning({
  items,
  units,
  stackingEnabled = false,
  useLayoutBoundingBoxForCubicFeet = true,
  selectedFreightClass,
  usingEstimatedFreightClass = false,
  setValue,
  freightClassPath = 'ltl.commodity.freightClass',
  estimatedFreightClassPath,
  usingEstimatedFreightClassPath,
  showEstimatedFreightClassMessagePath,
}: UseTaiFreightClassWarningProps): UseTaiFreightClassWarningReturn {
  const isTai = useIsTaiTMS();
  const [userChangedClass, setUserChangedClass] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const prevCalculatedRef = useRef<Maybe<string>>(null);
  const prevSelectedRef = useRef<Maybe<string>>(null);

  // Calculate freight class from dimensions
  const calculatedFreightClass = useMemo(() => {
    if (!isTai) {
      return null;
    }

    const metrics = calculateLTLMetrics(items, units, {
      useLayoutBoundingBoxForCubicFeet,
      stackingEnabled,
    });
    return metrics.freightClass ? String(metrics.freightClass) : null;
  }, [items, units, isTai, stackingEnabled, useLayoutBoundingBoxForCubicFeet]);

  useEffect(() => {
    if (!isTai || !estimatedFreightClassPath) {
      return;
    }

    setValue(estimatedFreightClassPath, calculatedFreightClass ?? '', {
      shouldValidate: false,
      shouldDirty: false,
    });
  }, [calculatedFreightClass, estimatedFreightClassPath, isTai, setValue]);

  // Auto-populate freight class on initial calculation
  useEffect(() => {
    if (!isTai) {
      return;
    }

    if (
      !calculatedFreightClass ||
      !(LTL_FREIGHT_CLASSES as readonly string[]).includes(
        calculatedFreightClass
      )
    ) {
      return;
    }

    const calculatedChanged =
      prevCalculatedRef.current !== calculatedFreightClass;

    if (calculatedChanged) {
      // A new estimated class means any previous dismissal should no longer suppress warnings.
      setWarningDismissed(false);
    }

    if (!selectedFreightClass) {
      setValue(
        freightClassPath,
        calculatedFreightClass as (typeof LTL_FREIGHT_CLASSES)[number],
        { shouldValidate: false, shouldDirty: false }
      );
      if (usingEstimatedFreightClassPath) {
        setValue(usingEstimatedFreightClassPath, true, {
          shouldValidate: false,
          shouldDirty: false,
        });
      }
      setUserChangedClass(false);
      prevSelectedRef.current = calculatedFreightClass;
    } else if (
      prevSelectedRef.current &&
      calculatedChanged &&
      !userChangedClass &&
      usingEstimatedFreightClass
    ) {
      // Keep in sync only while the user hasn't manually overridden the class.
      if (selectedFreightClass !== calculatedFreightClass) {
        setValue(
          freightClassPath,
          calculatedFreightClass as (typeof LTL_FREIGHT_CLASSES)[number],
          { shouldValidate: false, shouldDirty: false }
        );
        setUserChangedClass(false);
        prevSelectedRef.current = calculatedFreightClass;
      }
    }

    prevCalculatedRef.current = calculatedFreightClass;
  }, [
    calculatedFreightClass,
    isTai,
    selectedFreightClass,
    usingEstimatedFreightClass,
    setValue,
    userChangedClass,
    freightClassPath,
    usingEstimatedFreightClassPath,
  ]);

  // Detect changes and update warning state
  useEffect(() => {
    if (!isTai || !selectedFreightClass) {
      return;
    }

    if (prevSelectedRef.current !== selectedFreightClass) {
      prevSelectedRef.current = selectedFreightClass;

      if (calculatedFreightClass) {
        const differs = selectedFreightClass !== calculatedFreightClass;
        setUserChangedClass(differs);
        setWarningDismissed(false);
      }
    }
  }, [selectedFreightClass, calculatedFreightClass, isTai, setValue]);

  const handleUseEstimatedClass = () => {
    if (
      calculatedFreightClass &&
      LTL_FREIGHT_CLASSES.includes(
        calculatedFreightClass as (typeof LTL_FREIGHT_CLASSES)[number]
      )
    ) {
      setValue(
        freightClassPath,
        calculatedFreightClass as (typeof LTL_FREIGHT_CLASSES)[number]
      );
      if (usingEstimatedFreightClassPath) {
        setValue(usingEstimatedFreightClassPath, true, {
          shouldValidate: false,
          shouldDirty: false,
        });
      }
      setUserChangedClass(false);
      setWarningDismissed(false);
      prevSelectedRef.current = calculatedFreightClass;
    }
  };

  const handleDismissWarning = () => {
    setWarningDismissed(true);
  };

  const showWarning =
    isTai &&
    !!calculatedFreightClass &&
    !!selectedFreightClass &&
    selectedFreightClass !== calculatedFreightClass &&
    !warningDismissed;

  useEffect(() => {
    if (!isTai || !showEstimatedFreightClassMessagePath) {
      return;
    }

    setValue(showEstimatedFreightClassMessagePath, showWarning, {
      shouldValidate: false,
      shouldDirty: false,
    });
  }, [isTai, setValue, showEstimatedFreightClassMessagePath, showWarning]);

  return {
    calculatedFreightClass,
    showWarning,
    handleUseEstimatedClass,
    handleDismissWarning,
  };
}
